/*
 * Roth card fit: cut-out product photos (white / light studio background, soft
 * floor shadow allowed) stay object-fit: contain (theme default); lifestyle
 * photos get object-fit: cover.
 *
 * Detection: a tiny (64px) CDN version of the image is sampled on a canvas.
 * Classification starts immediately (independent of the full-size image load),
 * results are cached in localStorage. A CSS rule (roth-overrides.css) hides tile
 * images until data-roth-fit is set (with a 1.2s CSS failsafe).
 */
(function () {
  'use strict';

  // Product-tile images only (card-gallery = product cards, resource-card = search results).
  var SELECTORS = [
    '.card-gallery .product-media-container img',
    '.resource-card[data-resource-type="product"] img.resource-card__image'
  ];
  // Never touch anything inside these (keep in sync with the CSS in roth-overrides.css).
  var EXCLUDE = 'header, #header-group, .header, media-gallery, product-information, .product-information, dialog, [role="dialog"], footer';

  // ---- Tunable thresholds -------------------------------------------------
  var SAMPLE_WIDTH = 64;     // width param of the sample URL (px)
  var MAX_CANVAS = 64;       // canvas long side (px)
  var RING = 2;              // thickness of each outer side strip (px)
  var ALPHA_MAX = 20;        // alpha < 20 => background
  var LIGHT_MIN = 212;       // min(r,g,b) >= 212 ...
  var CHROMA_MAX = 18;       // ... and max-min <= 18 => background (light grey / tinted backdrops ok)
  var SIDE_CLEAN = 0.88;     // a side is "clean" if >= 88% of its pixels are background
  var SIDES_NEEDED = 3;      // cut-out if >= 3 of 4 sides are clean
  var TOTAL_BG = 0.62;       // ... or if >= 62% of ALL pixels are background (big floor shadow)
  var MAX_INFLIGHT = 8;      // max concurrent non-visible sample fetches (visible ones bypass)
  // Team 7 studio photos (filename contains "team7") are always cut-outs, unless the
  // filename hints at a room scene. Extra force lists can be added here.
  var FORCE_CONTAIN = /team7/i;
  var FORCE_COVER_EXCEPT = /location|ambiente|interior|setting|atmosphere|lifestyle|wohnbild|stuhl|stuehle|girado|lui-|leger/i;
  // Images forced to fill the tile (showroom photos with white walls etc.)
  var FORCE_COVER = /schauraum|miro_c/i;
  var CACHE_PREFIX = 'rothFit6:';
  var CACHE_MAX = 800;       // clear all cached entries if more than this
  // --------------------------------------------------------------------------

  var ATTR = 'data-roth-fit';    // contain | cover, set only AFTER the decision
  var SEEN = 'data-roth-seen';   // path key of the image already queued / classified

  var SELECTOR = SELECTORS.join(',');
  var scheduled = false;
  var inflight = 0;
  var queue = [];
  var cacheCount = 0;
  var anchor = document.createElement('a');

  // ---- localStorage helpers ----
  function cacheInit() {
    try {
      var n = 0, i, k;
      for (i = 0; i < localStorage.length; i++) {
        k = localStorage.key(i);
        if (k && k.indexOf(CACHE_PREFIX) === 0) n++;
      }
      if (n > CACHE_MAX) {
        var del = [];
        for (i = 0; i < localStorage.length; i++) {
          k = localStorage.key(i);
          if (k && k.indexOf(CACHE_PREFIX) === 0) del.push(k);
        }
        for (i = 0; i < del.length; i++) localStorage.removeItem(del[i]);
        n = 0;
      }
      cacheCount = n;
    } catch (e) { /* storage unavailable */ }
  }

  function cacheGet(key) {
    try {
      var v = localStorage.getItem(CACHE_PREFIX + key);
      return (v === 'c' || v === 'l') ? v : null;
    } catch (e) {
      return null;
    }
  }

  function cacheSet(key, v) {
    try {
      if (cacheCount > CACHE_MAX) {
        var del = [], i, k;
        for (i = 0; i < localStorage.length; i++) {
          k = localStorage.key(i);
          if (k && k.indexOf(CACHE_PREFIX) === 0) del.push(k);
        }
        for (i = 0; i < del.length; i++) localStorage.removeItem(del[i]);
        cacheCount = 0;
      }
      if (localStorage.getItem(CACHE_PREFIX + key) === null) cacheCount++;
      localStorage.setItem(CACHE_PREFIX + key, v);
    } catch (e) { /* ignore */ }
  }

  // ---- URL helpers ----
  function isCardImage(img) {
    try {
      if (!img.closest) return false;
      if (img.closest(EXCLUDE)) return false;
      return true;
    } catch (e) {
      return false;
    }
  }

  function firstSrcsetUrl(srcset) {
    if (!srcset) return '';
    var first = String(srcset).split(',')[0].replace(/^\s+/, '');
    return first.split(/\s+/)[0] || '';
  }

  function sourceOf(img) {
    return img.currentSrc || img.getAttribute('src') || firstSrcsetUrl(img.getAttribute('srcset')) || '';
  }

  function sampleUrl(src) {
    if (!src) return '';
    if (/^data:/i.test(src)) return src;
    if (/cdn\.shopify\.com|\/cdn\/shop\//i.test(src)) {
      if (/[?&]width=\d+/.test(src)) {
        return src.replace(/([?&])width=\d+/, '$1width=' + SAMPLE_WIDTH);
      }
      return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + SAMPLE_WIDTH;
    }
    return src;
  }

  // pathname without query string
  function keyOf(src) {
    try {
      anchor.href = src;
      return anchor.pathname || String(src).split('?')[0];
    } catch (e) {
      return String(src).split('?')[0];
    }
  }

  // ---- classifier ----
  function isBackground(d, i) {
    if (d[i + 3] < ALPHA_MAX) return true;
    var r = d[i], g = d[i + 1], b = d[i + 2];
    var mx = Math.max(r, g, b);
    var mn = Math.min(r, g, b);
    return mn >= LIGHT_MIN && (mx - mn) <= CHROMA_MAX;
  }

  // Returns true (cut-out), false (lifestyle) or null (unknown / error).
  function analyse(probe) {
    try {
      var nw = probe.naturalWidth, nh = probe.naturalHeight;
      if (!nw || !nh) return null;
      var scale = Math.min(1, MAX_CANVAS / Math.max(nw, nh));
      var w = Math.max(1, Math.round(nw * scale));
      var h = Math.max(1, Math.round(nh * scale));
      if (w < 8 || h < 8) return null;
      var canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      var ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(probe, 0, 0, w, h);
      var data = ctx.getImageData(0, 0, w, h).data;

      var topN = 0, topBg = 0, botN = 0, botBg = 0, leftN = 0, leftBg = 0, rightN = 0, rightBg = 0;
      var allBg = 0, x, y, bg;
      for (y = 0; y < h; y++) {
        for (x = 0; x < w; x++) {
          bg = isBackground(data, (y * w + x) * 4) ? 1 : 0;
          allBg += bg;
          // corners count for both adjacent sides
          if (y < RING) { topN++; topBg += bg; }
          if (y >= h - RING) { botN++; botBg += bg; }
          if (x < RING) { leftN++; leftBg += bg; }
          if (x >= w - RING) { rightN++; rightBg += bg; }
        }
      }
      var clean = 0;
      if (topN && topBg / topN >= SIDE_CLEAN) clean++;
      if (botN && botBg / botN >= SIDE_CLEAN) clean++;
      if (leftN && leftBg / leftN >= SIDE_CLEAN) clean++;
      if (rightN && rightBg / rightN >= SIDE_CLEAN) clean++;
      if (clean >= SIDES_NEEDED) return true;
      if (allBg / (w * h) >= TOTAL_BG) return true;
      return false;
    } catch (e) {
      return null;
    }
  }

  // inline style first, then the attribute (which reveals the image via CSS)
  function apply(img, isCutout) {
    try {
      if (isCutout) {
        img.style.removeProperty('object-fit');
        img.setAttribute(ATTR, 'contain');
      } else {
        img.style.setProperty('object-fit', 'cover', 'important');
        img.setAttribute(ATTR, 'cover');
      }
    } catch (e) { /* ignore */ }
  }

  function reveal(img) {
    try { img.setAttribute(ATTR, 'contain'); } catch (e) { /* ignore */ }
  }

  // ---- fetch queue ----
  function pump() {
    while (inflight < MAX_INFLIGHT && queue.length) {
      start(queue.shift());
    }
  }

  function start(job) {
    inflight++;
    var done = false;
    var probe = new Image();
    function finish() {
      if (done) return false;
      done = true;
      inflight--;
      return true;
    }
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      if (!finish()) return;
      var res = analyse(probe);
      if (res === null) {
        reveal(job.img);
      } else {
        cacheSet(job.key, res ? 'c' : 'l');
        // only apply if the image still points to the same file
        if (job.img.getAttribute(SEEN) === job.key) apply(job.img, res);
      }
      pump();
    };
    probe.onerror = function () {
      if (!finish()) return;
      reveal(job.img);
      pump();
    };
    probe.src = job.url;
  }

  function inViewport(img) {
    try {
      var r = img.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var vw = window.innerWidth || document.documentElement.clientWidth;
      return r.bottom > 0 && r.right > 0 && r.top < vh && r.left < vw;
    } catch (e) {
      return false;
    }
  }

  function enqueue(img, key, url) {
    var job = { img: img, key: key, url: url };
    if (inViewport(img)) {
      start(job);            // visible ones are never delayed
    } else {
      queue.push(job);
      pump();
    }
  }

  function process(img) {
    try {
      var src = sourceOf(img);
      if (!src) return;
      var key = keyOf(src);
      if (img.getAttribute(SEEN) === key) return;
      if (!isCardImage(img)) return;
      img.setAttribute(SEEN, key);

      if (FORCE_COVER.test(key)) {
        apply(img, false);
        return;
      }
      if (FORCE_CONTAIN.test(key) && !FORCE_COVER_EXCEPT.test(key)) {
        apply(img, true);
        return;
      }
      var cached = cacheGet(key);
      if (cached) {
        apply(img, cached === 'c');
        return;
      }
      enqueue(img, key, sampleUrl(src));
    } catch (e) {
      /* leave untouched; CSS failsafe reveals it */
    }
  }

  function scan() {
    scheduled = false;
    try {
      var imgs = document.querySelectorAll(SELECTOR);
      for (var i = 0; i < imgs.length; i++) process(imgs[i]);
    } catch (e) {
      /* ignore */
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(scan, 16);
  }

  cacheInit();
  scan();   // start right away, no waiting for load events

  if (window.MutationObserver) {
    try {
      new MutationObserver(schedule).observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['src', 'srcset']
      });
    } catch (e) { /* ignore */ }
  }
  document.addEventListener('DOMContentLoaded', scan);
  document.addEventListener('shopify:section:load', schedule);
})();
